import { CartQueue, itemIdentity } from 'aw-cart-queue';

const root = window.Shopify?.routes?.root || '/';
const selector = 'aw-cart-quantity';
const controlStates = new WeakMap();
const rowCopies = new Map();
let rendering = false;
let navigationPending = false;
const bypassForms = new WeakSet();
const statusText = 'Updating your cart…';

async function request(path, body) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 16000);
  try {
    const response = await fetch(path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body ? {'Content-Type': 'application/json'} : {}) },
      ...(body ? {body: JSON.stringify(body)} : {}), signal: controller.signal
    });
    const result = await response.json();
    if (!response.ok) throw Object.assign(new Error(typeof result.description === 'string' ? result.description : (typeof result.message === 'string' ? result.message : 'Your cart could not be updated. Please try again.')), {status: response.status});
    return result;
  } finally { clearTimeout(timer); }
}

function sectionIds() {
  return [...new Set([...document.querySelectorAll('[data-aw-cart-surface]')].map(el => el.closest('.shopify-section')?.id.replace('shopify-section-', '')).filter(Boolean))].slice(0, 5);
}

function cartEvent(cart) {
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {bubbles: true, detail: {baseEvent: 'aw:quantity', cart}}));
}

const queue = new CartQueue({
  read: () => request(`${root}cart.js`),
  write: async (kind, item, quantity) => {
    const bundled = {sections: sectionIds(), sections_url: location.pathname};
    if (kind === 'change') return request(`${root}cart/change.js`, {id: item.key, quantity, ...bundled});
    const addition = {id: item.variant_id || item.id, quantity, properties: item.properties || {}};
    const plan = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan;
    if (plan) addition.selling_plan = plan;
    const result = await request(`${root}cart/add.js`, {items: [addition], ...bundled});
    const cart = await request(`${root}cart.js`);
    cart.sections = result.sections;
    return cart;
  },
  render: renderCart,
  notify: updateControls
});

window.AnywellCart = {version: '20260908.2', get busy() {return queue.busy;}, get error() {return queue.error;}, whenIdle: () => queue.idle()};

function scan() {
  document.querySelectorAll(selector).forEach(el => {
    if (controlStates.has(el)) return;
    let properties = {};
    try { properties = JSON.parse(el.dataset.properties || '{}') || {}; } catch { /* Existing cart details stay authoritative. */ }
    const state = queue.register({key: el.dataset.key, variant_id: Number(el.dataset.variant), quantity: Number(el.querySelector('[data-aw-quantity]').value), properties, selling_plan: el.dataset.sellingPlan || null}, {
      minimum: Number(el.dataset.minimum) || 1, increment: Number(el.dataset.increment) || 1,
      maximum: el.hasAttribute('data-maximum') ? Number(el.dataset.maximum) : null
    });
    controlStates.set(el, state);
    if (!el.closest('[data-aw-zero]')) saveRow(el, state);
  });
}

function saveRow(el, state) {
  const surface = el.closest('[data-aw-cart-surface]');
  if (!surface) return;
  const row = surface.dataset.awCartSurface === 'page' ? el.closest('tr') : el.closest('line-item');
  if (!row) return;
  const copies = rowCopies.get(state.uid) || new Map();
  copies.set(surface.dataset.awCartSurface, row.cloneNode(true));
  rowCopies.set(state.uid, copies);
}

function feedback(surface) {
  let el = surface.querySelector('.aw-cart-feedback');
  if (!el) {
    el = document.createElement('div');
    el.className = 'aw-cart-feedback';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.innerHTML = '<span></span><button type="button" data-aw-check hidden>Check cart</button>';
    const heading = surface.querySelector('.cart-header, .cart-drawer__top');
    if (heading) heading.after(el); else surface.prepend(el);
  }
  return el;
}

function updateControls() {
  scan();
  document.querySelectorAll(selector).forEach(el => {
    const state = controlStates.get(el);
    if (!state) return;
    const input = el.querySelector('[data-aw-quantity]');
    if (document.activeElement !== input || !queue.error) input.value = state.desired;
    input.disabled = queue.uncertain;
    const minus = el.querySelector('[data-aw-minus]');
    const plus = el.querySelector('[data-aw-plus]');
    plus.disabled = queue.uncertain || (state.maximum != null && state.desired >= state.maximum);
    if (!state.desired && document.activeElement === minus && !plus.disabled) plus.focus({preventScroll: true});
    minus.disabled = queue.uncertain || state.desired === 0;
    el.querySelector('[data-aw-remove]').disabled = queue.uncertain;
    const zero = !state.desired && !state.confirmed && state.keepZero;
    el.querySelector('.aw-zero-note').hidden = !zero;
    el.toggleAttribute('data-aw-zero', zero);
    el.setAttribute('aria-busy', String(state.desired !== state.confirmed));
  });
  document.querySelectorAll('[data-aw-cart-surface]').forEach(surface => {
    const status = feedback(surface);
    status.hidden = !queue.busy && !queue.error;
    status.toggleAttribute('data-error', Boolean(queue.error));
    status.querySelector('span').textContent = queue.error || statusText;
    status.querySelector('button').hidden = !queue.error;
    status.querySelector('button').disabled = queue.busy;
    surface.querySelectorAll('.cart-form__totals, [slot="footer"]').forEach(el => el.setAttribute('aria-busy', String(queue.busy)));
  });
  const empty = queue.cart ? queue.cart.item_count === 0 && ![...queue.states.values()].some(s => s.desired > 0) : false;
  document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(el => {
    if (empty) { el.dataset.awEmptyDisabled = 'true'; el.disabled = true; }
    else if (el.dataset.awEmptyDisabled) { el.disabled = false; delete el.dataset.awEmptyDisabled; }
    el.setAttribute('aria-busy', String(queue.busy));
  });
}

function saveFocus() {
  const active = document.activeElement;
  const el = active?.closest(selector);
  const state = el && controlStates.get(el);
  return state ? {uid: state.uid, surface: el.closest('[data-aw-cart-surface]')?.dataset.awCartSurface, action: active.hasAttribute('data-aw-plus') ? '[data-aw-plus]' : active.hasAttribute('data-aw-minus') ? '[data-aw-minus]' : active.hasAttribute('data-aw-remove') ? '[data-aw-remove]' : '[data-aw-quantity]'} : null;
}

function restoreFocus(focus) {
  if (!focus) return;
  const candidate = [...document.querySelectorAll(selector)].find(el => controlStates.get(el)?.uid === focus.uid && el.closest('[data-aw-cart-surface]')?.dataset.awCartSurface === focus.surface && el.getClientRects().length && !el.closest('[inert]') && getComputedStyle(el).visibility !== 'hidden');
  const target = candidate?.querySelector(focus.action);
  if (target && !target.disabled && target.getClientRects().length) target.focus({preventScroll: true});
  else candidate?.querySelector('[data-aw-plus]')?.focus({preventScroll: true});
}

function preserveForms(container) {
  return [...container.querySelectorAll('input:not([data-aw-quantity]):not([type="hidden"]):not([type="submit"]), textarea, select')].map(el => ({id: el.id, name: el.name, value: el.value, checked: el.checked, type: el.type})).filter(el => el.id || el.name);
}

function restoreForms(container, values) {
  for (const saved of values) {
    const el = [...container.querySelectorAll('input, textarea, select')].find(el => saved.id ? el.id === saved.id : el.name === saved.name);
    if (!el) continue;
    el.value = saved.value;
    if (saved.type === 'checkbox' || saved.type === 'radio') el.checked = saved.checked;
  }
}

function appendZeroRows() {
  document.querySelectorAll('[data-aw-zero-row], .aw-cart-zero-list').forEach(el => el.remove());
  for (const state of queue.states.values()) {
    if (state.confirmed || state.desired || !state.keepZero) continue;
    for (const surface of document.querySelectorAll('[data-aw-cart-surface]')) {
      const type = surface.dataset.awCartSurface;
      const saved = rowCopies.get(state.uid)?.get(type);
      if (!saved) continue;
      const row = saved.cloneNode(true);
      row.setAttribute('data-aw-zero-row', '');
      row.setAttribute('data-aw-zero', '');
      row.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      row.querySelectorAll(selector).forEach(el => {
        el.dataset.key = state.item.key;
        controlStates.set(el, state);
        el.querySelector('[data-aw-quantity]').value = 0;
      });
      // Keep only product details and a truthful zero line total for the temporary row.
      row.querySelectorAll('price-list, .badge, [role="alert"]').forEach(el => el.remove());
      if (type === 'page') {
        const priceCell = row.querySelector('td:last-child');
        if (priceCell) priceCell.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency: queue.cart.currency || 'USD'}).format(0);
      }
      let list = surface.querySelector(type === 'page' ? '.order-summary__body' : '.cart-drawer__line-items');
      if (!list) {
        let emptyList = surface.querySelector('.aw-cart-zero-list');
        if (!emptyList) {
          emptyList = document.createElement(type === 'page' ? 'table' : 'div');
          emptyList.className = 'aw-cart-zero-list';
          if (type === 'page') emptyList.appendChild(document.createElement('tbody'));
          const emptyState = surface.querySelector('.empty-state');
          if (emptyState) emptyState.appendChild(emptyList); else surface.appendChild(emptyList);
        }
        list = type === 'page' ? emptyList.querySelector('tbody') : emptyList;
      }
      list.appendChild(row);
    }
  }
}

async function renderCart(cart, sections) {
  const ids = sectionIds();
  if (!sections || ids.some(id => typeof sections[id] !== 'string')) {
    const url = new URL(location.href);
    url.searchParams.set('sections', ids.join(','));
    sections = await request(url.pathname + url.search);
  }
  if (ids.some(id => typeof sections[id] !== 'string')) throw new Error('Your cart was updated, but the totals could not be displayed. Check cart to refresh them.');
  const replacements = [];
  for (const id of ids) {
    const current = document.getElementById(`shopify-section-${id}`);
    const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
    const next = parsed.getElementById(`shopify-section-${id}`);
    if (!current || !next) throw new Error('Your cart was updated. Check cart to reload the order summary.');
    replacements.push({current, next});
  }
  const focus = saveFocus();
  rendering = true;
  try {
    for (const {current, next} of replacements) {
      const fields = preserveForms(current);
      const drawer = current.querySelector('cart-drawer');
      if (drawer) drawer.replaceChildren(...next.querySelector('cart-drawer').childNodes);
      else current.replaceChildren(...next.childNodes);
      restoreForms(current, fields);
    }
    scan();
    appendZeroRows();
    updateControls();
    restoreFocus(focus);
    cart.sections = sections;
    cartEvent(cart);
  } finally { rendering = false; }
}

document.addEventListener('click', event => {
  const check = event.target.closest?.('[data-aw-check]');
  if (check) { event.preventDefault(); queue.check(); return; }
  const button = event.target.closest?.('[data-aw-plus], [data-aw-minus], [data-aw-remove]');
  const el = button?.closest(selector);
  if (!el) return;
  event.preventDefault();
  scan();
  const state = controlStates.get(el);
  if (!state || button.disabled) return;
  saveRow(el, state);
  if (button.hasAttribute('data-aw-remove')) queue.set(state, 0, false);
  else if (button.hasAttribute('data-aw-plus')) queue.set(state, state.desired === 0 ? state.minimum : state.desired + state.increment);
  else queue.set(state, state.desired <= state.minimum ? 0 : state.desired - state.increment);
});

document.addEventListener('change', event => {
  if (!event.target.matches?.('[data-aw-quantity]')) return;
  scan();
  const el = event.target.closest(selector);
  const state = controlStates.get(el);
  saveRow(el, state);
  queue.set(state, event.target.value.trim() === '' ? NaN : Number(event.target.value));
});

document.addEventListener('keydown', event => {
  if (event.key === 'Enter' && event.target.matches?.('[data-aw-quantity]')) {
    event.preventDefault();
    event.target.blur();
  }
});

function allowedCheckout() {
  if (!queue.cart || queue.cart.item_count > 0) return true;
  queue.error = 'Your cart is empty. Add an item before checkout.';
  updateControls();
  return false;
}

document.addEventListener('submit', event => {
  const form = event.target;
  const submitter = event.submitter;
  if (!(form instanceof HTMLFormElement) || bypassForms.has(form)) return;
  const action = new URL(form.action, location.href);
  if (action.origin !== location.origin) return;
  const checkout = submitter?.name === 'checkout' || /\/checkout\/?$/.test(action.pathname);
  const add = /\/cart\/add\/?$/.test(action.pathname);
  if (!checkout && !add) return;
  if (!queue.busy && !queue.error && !queue.uncertain) {
    if (checkout && !allowedCheckout()) event.preventDefault();
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  if (navigationPending) return;
  navigationPending = true;
  const sectionId = form.closest('.shopify-section')?.id;
  queue.idle().then(ok => {
    if (!ok || (checkout && !allowedCheckout())) return;
    const scope = sectionId ? document.getElementById(sectionId) : document;
    const current = form.isConnected ? form : [...scope.querySelectorAll('form')].find(el => el.action === action.href && (checkout ? el.querySelector('[name="checkout"]') : true));
    if (!current) return;
    const button = submitter?.isConnected ? submitter : current.querySelector('[name="checkout"]');
    bypassForms.add(current);
    try { current.requestSubmit(button || undefined); } finally { bypassForms.delete(current); }
  }).finally(() => { navigationPending = false; });
}, true);

document.addEventListener('click', event => {
  const link = event.target.closest?.('a[href]');
  if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin || !/\/(cart|checkout)\/?$/.test(url.pathname)) return;
  if (link.getAttribute('aria-controls') === 'cart-drawer') return;
  if (!queue.busy && !queue.error && !queue.uncertain) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (navigationPending) return;
  navigationPending = true;
  queue.idle().then(ok => { if (ok && (!/\/checkout\/?$/.test(url.pathname) || allowedCheckout())) location.assign(url.href); }).finally(() => { navigationPending = false; });
}, true);

document.addEventListener('cart:change', event => {
  if (event.detail?.baseEvent === 'aw:quantity' || rendering) return;
  if (!queue.busy && event.detail?.cart) {
    try { queue.adopt(event.detail.cart); } catch { queue.error = 'Your cart changed. Check cart to continue.'; }
    queueMicrotask(updateControls);
  }
});
window.addEventListener('pageshow', event => {
  navigationPending = false;
  if (event.persisted) return;
});
const observer = new MutationObserver(records => {
  if (!rendering && records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))))) updateControls();
});
observer.observe(document.body, {subtree: true, childList: true});
updateControls();
